"use client";

import React from "react";
import { Children, use, useState } from "react";
import styles from "./ProductListingPage.module.css";
import { InfiniteProductList } from "./InfiniteProductList";

import { SegmentControlContext } from "@/components/SegmentControl";
import { matchSegments } from "@/components/SegmentControl/control";
import type { Segments } from "@/components/SegmentControl/types";
import { useMediaQuery } from "@frend-digital/ui/hooks";
import { useProducts } from "./ListingContext";

type ProductListProps = {
	content?: React.ReactNode[];
	/**
	 * The raw content bloks, aligned 1:1 with `content`. Used to evaluate each
	 * blok's segment so cards denied for the current market are filtered out
	 * before positioning, instead of leaving a blank gap in the grid.
	 */
	bloks?: Segments[];
	preview: boolean;
	noResults?: React.ReactNode;
};

export const ProductList = ({ content, bloks, preview }: ProductListProps) => {
	const [showMore, setShowMore] = useState(false);
	const isMobile = useMediaQuery("(max-width: 1024px)");
	const { query } = useProducts();
	const { segments } = use(SegmentControlContext);

	const totalProducts = query.data?.pages.reduce((acc, page) => acc + page.products.length, 0) ?? 0;

	const productsPerRow = isMobile ? 2 : 4;
	const productRows = Math.ceil(totalProducts / productsPerRow);

	// Drop content cards whose segment denies the current market so the
	// country-appropriate card takes the slot (no blank gap left behind).
	const visibleContent = Children.toArray(content).filter((_, index) => {
		const blok = bloks?.[index];
		if (!blok) return true;
		return matchSegments(blok, segments).rule !== "DENY";
	});

	return (
		<>
			<InfiniteProductList preview={preview} showMore={showMore} setShowMore={setShowMore} />
			{visibleContent.map((child, index) => {
				const gridRow = index === 0 ? 3 : 3 + index * 4;
				const hasEnoughProducts = gridRow <= productRows;

				const key = React.isValidElement(child) && child.key ? child.key : index;

				return (
					<div
						key={key}
						className={styles.content}
						style={
							{
								gridColumn: isMobile ? "1 / span 6" : index % 2 === 0 ? "1 / span 6" : "7 / span 6",
								display: hasEnoughProducts ? "grid" : "none",
								gridRow: `${gridRow} / span 2`,
							} as React.CSSProperties
						}
					>
						{child}
					</div>
				);
			})}
		</>
	);
};
